import React, { useEffect, useMemo, useState } from 'react';
import { FiPlus, FiEdit2, FiEye } from 'react-icons/fi';
import api, { ItemsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import EditItemModal from '../components/pos/EditItemModal';
import './Inventory.css';

export default function Inventory() {
  const { company } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all'); // all | product | service
  const [lowOnly, setLowOnly] = useState(false);
  const [toast, setToast] = useState('');

  const smallBtn = { fontSize: 12, padding: '4px 10px', borderRadius: 8, height: 25, display: 'inline-flex', alignItems: 'center', gap: 6 };
  const controlStyle = { fontSize: 13, padding: '6px 8px', height: 34 };
  const controlStyleSmall = { fontSize: 12, padding: '4px 8px', height: 30 };

  const [editor, setEditor] = useState({ open: false, mode: 'view', data: null, saving: false });

  const fetchItems = async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const data = await ItemsAPI.list({ companyId: company.id });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load items', e);
      setToast('Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, [company?.id]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (type === 'service' && !it.isService) return false;
      if (type === 'product' && it.isService) return false;
      if (!q) return true;
      return (
        (it.name || '').toLowerCase().includes(q) ||
        (it.description || '').toLowerCase().includes(q) ||
        (it.hsnsac || '').toLowerCase().includes(q)
      );
    });
  }, [items, search, type]);

  const isLowInventory = (it) => {
    if (it?.isService) return false;
    const qty = Number(it?.inventory ?? 0);
    return qty < 10; // includes 0
  };

  const lowCount = useMemo(() => filtered.filter(isLowInventory).length, [filtered]);

  const visible = useMemo(() => {
    if (!lowOnly) return filtered;
    return filtered.filter(isLowInventory);
  }, [filtered, lowOnly]);

  const formatINR = (val) => {
    try { return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(Number(val || 0)); } catch { return val; }
  };

  const getPrimaryImage = (it) => (Array.isArray(it?.images) && it.images.length ? it.images[0] : '');
  const toggleType = (next) => setType((prev) => (prev === next ? 'all' : next));
  const clearFilters = () => { setSearch(''); setType('all'); setLowOnly(false); };

  const blankItem = () => ({
    companyId: company?.id || null,
    name: '',
    description: '',
    hsnsac: '',
    unit: 'NOS',
    price: 0,
    inventory: 0,
    isService: false,
    cgstRate: 0,
    sgstRate: 0,
    igstRate: 0,
    cessRate: 0,
    images: []
  });

  const openNew = () => {
    setEditor({ open: true, mode: 'new', data: blankItem(), saving: false });
  };
  const openView = (it) => {
    setEditor({ open: true, mode: 'view', data: { ...it }, saving: false });
  };
  const openEdit = (it) => {
    setEditor({ open: true, mode: 'edit', data: { ...it }, saving: false });
  };
  const closeEditor = () => {
    setEditor({ open: false, mode: 'view', data: null, saving: false });
  };

  const handleChange = (field, value) => {
    setEditor((s) => ({ ...s, data: { ...s.data, [field]: value } }));
  };

  const handleRemoveImage = (idx) => {
    if (editor.mode === 'view') return;
    setEditor((s) => {
      const imgs = Array.isArray(s.data?.images) ? [...s.data.images] : [];
      imgs.splice(idx, 1);
      return { ...s, data: { ...s.data, images: imgs } };
    });
  };

  const handleAddImages = async (files) => {
    if (!files || files.length === 0) return;
    const dir = `items/${company?.id || 'uploads'}`;
    const newUrls = [];
    for (const file of files) {
      try {
        const { data } = await api.get('/api/upload/presigned-url', { params: { fileType: file.type, dir } });
        const { uploadUrl, fileUrl } = data || {};
        if (!uploadUrl || !fileUrl) continue;
        const putRes = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file
        });
        if (putRes.ok) {
          newUrls.push(fileUrl);
        }
      } catch (e) {
        console.error('Image upload failed', e);
      }
    }
    if (newUrls.length) {
      setEditor((s) => {
        const imgs = Array.isArray(s.data?.images) ? s.data.images : [];
        return { ...s, data: { ...s.data, images: [...imgs, ...newUrls] } };
      });
    }
  };

  const openFilePicker = () => {
    if (editor.mode === 'view') return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = (e) => {
      const f = e.target.files;
      if (f && f.length) {
        handleAddImages(Array.from(f));
      }
    };
    input.click();
  };

  const saveEditor = async (itemFromModal) => {
    // Use itemFromModal if provided, otherwise fall back to editor.data
    const data = itemFromModal || editor.data;
    if (!data?.name) {
      setToast('Name is required');
      return;
    }
    setEditor((s) => ({ ...s, saving: true }));
    try {
      const payload = { ...data };
      // Ensure numeric fields
      const num = (v) => (v === '' || v === null || v === undefined ? 0 : Number(v));
      payload.price = num(payload.price);
      payload.inventory = payload.isService ? 0 : num(payload.inventory);
      payload.cgstRate = num(payload.cgstRate);
      payload.sgstRate = num(payload.sgstRate);
      payload.igstRate = num(payload.igstRate);
      payload.cessRate = num(payload.cessRate);
      payload.companyId = payload.companyId || company?.id;
      // Preserve images from editor.data (they're managed via handleChange)
      payload.images = editor.data?.images || [];

      if (editor.mode === 'new') {
        await ItemsAPI.create(payload);
        setToast('Item created');
      } else if (editor.mode === 'edit' && payload.id) {
        await ItemsAPI.update(payload.id, payload);
        setToast('Item updated');
      }
      await fetchItems();
      closeEditor();
    } catch (e) {
      console.error('Save failed', e);
      setToast('Failed to save item');
      setEditor((s) => ({ ...s, saving: false }));
    }
  };

  return (
    <div className="page-content">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 8 }}>
        <h2 style={{ fontSize: 20, margin: 0 }}>Inventory</h2>
        <button className="submit-button" style={{ textTransform:'none', display:'inline-flex', alignItems:'center', gap:6 }} onClick={openNew}>
          <FiPlus /> New Item
        </button>
      </div>

      <div className="filter-section inventory-filters">
        <div className="inventory-filters__row">
          <div className="inventory-filters__search">
            <label className="inventory-filters__label">Search</label>
            <input
              className="form-control"
              style={controlStyle}
              placeholder="Name / description / HSN-SAC"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="inventory-filters__type">
            <div className="inventory-filters__labelRow">
              <span className="inventory-filters__label">Type</span>
              {type === 'all' && <span className="inventory-filters__pill">All</span>}
            </div>
            <div className="inventory-type-toggle" role="group" aria-label="Type filter">
              <button
                type="button"
                className={`inventory-type-btn ${type === 'product' ? 'active' : ''}`}
                onClick={() => toggleType('product')}
              >
                Products
              </button>
              <button
                type="button"
                className={`inventory-type-btn ${type === 'service' ? 'active' : ''}`}
                onClick={() => toggleType('service')}
              >
                Services
              </button>
            </div>
          </div>

          <div className="inventory-filters__actions">
            <button className="btn btn-secondary" style={smallBtn} type="button" onClick={clearFilters}>
              Clear
            </button>
          </div>
        </div>

        <div className="inventory-filters__footer">
          <div className="inventory-filters__footerLeft">
            <div className="inventory-count">{visible.length} items</div>
            <button
              type="button"
              className={`inventory-filter-tab ${lowOnly ? 'active' : ''}`}
              onClick={() => setLowOnly((v) => !v)}
              disabled={lowCount === 0}
              title={lowCount === 0 ? 'No low inventory items in current filters' : 'Show only low inventory items'}
            >
              Low inventory
              <span className="inventory-filter-tab__count">{lowCount}</span>
            </button>
          </div>
          {!!toast && <div className="inventory-toast" role="status">{toast}</div>}
        </div>
      </div>

      <div className="form-section" style={{ overflowX:'auto', maxHeight: 520 }}>
        <div className="inventory-grid" aria-busy={loading ? 'true' : 'false'}>
          {!loading && visible.map((it) => {
            const img = getPrimaryImage(it);
            const qty = it.isService ? null : Number(it.inventory ?? 0);
            const lowStock = qty !== null && qty > 0 && qty < 10;
            const outOfStock = qty !== null && qty <= 0;
            const stockClass = outOfStock ? 'inventory-card--out' : lowStock ? 'inventory-card--low' : '';
            const typeClass = it.isService ? 'inventory-card--service' : 'inventory-card--product';
            return (
              <div
                key={it.id}
                className={`card inventory-card ${typeClass} ${stockClass}`}
                role="button"
                tabIndex={0}
                onClick={() => openView(it)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openView(it); }}
                aria-label={`View ${it.name || 'item'}`}
              >
                <div className="inventory-card__media" aria-hidden="true">
                  {img ? (
                    <img src={img} alt="" className="inventory-card__img" />
                  ) : (
                    <div className="inventory-card__placeholder">
                      <div className="inventory-card__placeholderText">{(it.name || 'Item').slice(0, 2).toUpperCase()}</div>
                    </div>
                  )}
                  <div className="inventory-card__topRow">
                    <div className={`inventory-badge ${it.isService ? 'service' : 'product'}`}>
                      {it.isService ? 'Service' : 'Product'}
                    </div>
                    <div className="inventory-card__topActions" aria-label="Item actions">
                      <FiEye
                        className="inventory-action-icon"
                        onClick={(e) => { e.stopPropagation(); openView(it); }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            openView(it);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label="View"
                        title="View"
                      />
                      <FiEdit2
                        className="inventory-action-icon"
                        onClick={(e) => { e.stopPropagation(); openEdit(it); }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            openEdit(it);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label="Edit"
                        title="Edit"
                      />
                    </div>
                  </div>
                </div>

                <div className="inventory-card__body">
                  <div className="inventory-card__titleRow">
                    <div className="inventory-card__title" title={it.name || ''}>{it.name || '-'}</div>
                    <div className="inventory-card__price">{formatINR(it.price)}</div>
                  </div>

                  {!!it.description && (
                    <div className="inventory-card__desc" title={it.description || ''}>{it.description}</div>
                  )}

                  <div className="inventory-card__meta">
                    <div className="inventory-card__metaItem">
                      <span className="inventory-card__metaLabel">HSN/SAC</span>
                      <span className="inventory-card__metaValue">{it.hsnsac || '-'}</span>
                    </div>
                    <div className="inventory-card__metaItem">
                      <span className="inventory-card__metaLabel">Unit</span>
                      <span className="inventory-card__metaValue">{it.unit || '-'}</span>
                    </div>
                    <div className="inventory-card__metaItem">
                      <span className="inventory-card__metaLabel">Stock</span>
                      <span className={`inventory-card__metaValue inventory-stock ${lowStock ? 'low' : ''}`}>
                        {it.isService ? '-' : (it.inventory ?? 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {(!loading && filtered.length === 0) && (
            <div className="inventory-empty">
              <div className="inventory-empty__title">No items found</div>
              <div className="inventory-empty__sub">Try adjusting search/type filters.</div>
            </div>
          )}

          {loading && (
            <div className="inventory-loading">
              <div className="loading-spinner" />
            </div>
          )}
        </div>
      </div>

      {editor.open && editor.data && (
        <EditItemModal
          item={editor.data}
          mode={editor.mode}
          onChange={handleChange}
          onClose={closeEditor}
          onSave={saveEditor}
          onModeChange={(newMode) => setEditor((s) => ({ ...s, mode: newMode }))}
          images={editor.data?.images || []}
          onAddImage={openFilePicker}
          onRemoveImage={handleRemoveImage}
          saving={editor.saving}
        />
      )}
    </div>
  );
}


